"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { connectModalStore } from "@layerswap/wallet-core";
import * as UiKit from "@layerswap/ui-kit/components";
import { widgetTelemetry } from '../../../lib/widgetTelemetry';
const ConnectModalContext = createContext(null);
export function WalletModalProvider({ children }) {
    return (_jsx(UiKit.WalletModalProvider, { children: _jsx(WalletModalShell, { children: children }) }));
}
function WalletModalShell({ children }) {
    const { selectedProvider, setSelectedProvider, selectedConnector, setSelectedConnector, selectedMultiChainConnector, setSelectedMultiChainConnector, start, cancel: cancelFlow, finish, goBack, } = UiKit.useConnectModal();
    const [open, setOpen] = useState(false);
    const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
    const [presentation, setPresentation] = useState("modal");
    const [placement, setPlacement] = useState("default");
    const [dismissible, setDismissible] = useState(true);
    const [topContent, setTopContent] = useState(null);
    const [fullHeight, setFullHeight] = useState(false);
    const [hideHeader, setHideHeader] = useState(false);
    const connect = useCallback(async (provider, options = {}) => {
        setPresentation("modal");
        setPlacement(options.placement ?? "default");
        setDismissible(options.dismissible ?? true);
        setTopContent(options.topContent ?? null);
        setFullHeight(options.fullHeight ?? false);
        setHideHeader(options.hideHeader ?? false);
        setOpen(true);
        const finishTelemetry = widgetTelemetry.beginOperation('wallet_connection', { timing_kind: 'user_wait_included' });
        try {
            const wallet = await start(provider);
            finishTelemetry(wallet ? 'succeeded' : 'cancelled');
            return wallet;
        }
        catch (error) {
            finishTelemetry('failed');
            throw error;
        }
    }, [start]);
    const cancel = useCallback(() => {
        cancelFlow();
        setOpen(false);
    }, [cancelFlow]);
    const onFinish = useCallback((wallet) => {
        finish(wallet);
        setOpen(false);
    }, [finish]);
    useEffect(() => {
        if (!open && (selectedConnector || selectedMultiChainConnector)) {
            setSelectedConnector(undefined);
            setSelectedMultiChainConnector(undefined);
            setSelectedProvider(undefined);
        }
        if (!open) {
            setPlacement("default");
            setDismissible(true);
            setTopContent(null);
            setFullHeight(false);
            setHideHeader(false);
            setPresentation("modal");
        }
        setIsWalletModalOpen(open);
        connectModalStore._syncOpen(open);
    }, [open]);
    const value = useMemo(() => ({
        connect,
        cancel,
        selectedProvider,
        setSelectedProvider,
        selectedConnector,
        setSelectedConnector,
        selectedMultiChainConnector,
        setSelectedMultiChainConnector,
        goBack,
        onFinish,
        setOpen,
        open,
        isWalletModalOpen,
        presentation,
        setPresentation,
        placement,
        dismissible,
        topContent,
        fullHeight,
        hideHeader,
    }), [
        cancel,
        connect,
        placement,
        dismissible,
        fullHeight,
        goBack,
        hideHeader,
        isWalletModalOpen,
        onFinish,
        open,
        presentation,
        selectedConnector,
        selectedMultiChainConnector,
        selectedProvider,
        topContent,
    ]);
    return _jsx(ConnectModalContext.Provider, { value: value, children: children });
}
export function useConnectModal() {
    const context = useContext(ConnectModalContext);
    if (!context)
        throw new Error("useConnectModal must be used within a ConnectModalProvider");
    return context;
}
